import axios from 'axios';

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8081';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add an interceptor to include the token in requests when available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post('/v1/api/login', { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userRole', response.data.role);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Login failed. Please try again.' };
    }
  },
  
  signup: async (email, password, invitation_token) => {
    try {
      const response = await api.post('/v1/api/sign-up', { 
        email, 
        password, 
        invitation_token 
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userRole', response.data.role);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Signup failed. Please try again.' };
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    sessionStorage.removeItem('selectedAssistant');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  getUserRole: () => {
    return localStorage.getItem('userRole');
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/v1/api/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get current user' };
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/v1/api/forget-password', { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to request password reset' };
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/v1/api/reset-password', { 
        token, 
        new_password: newPassword 
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reset password' };
    }
  }
};

export const assistantService = {
  // Get all assistants accessible to the user
  getMyAssistants: async () => {
    try {
      const response = await api.get('/v1/api/my-assistants');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assistants.' };
    }
  },

  // Get all assistants (super admin only)
  getAllAssistants: async () => {
    try {
      const response = await api.get('/v1/api/assistants');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assistants.' };
    }
  },

  // Get single assistant by ID
  getAssistant: async (assistantId) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assistant.' };
    }
  },

  // Alias for getAssistant
  getAssistantById: async (assistantId) => {
    return assistantService.getAssistant(assistantId);
  },

  // Create new assistant (super admin only)
  createAssistant: async (assistantData) => {
    try {
      const response = await api.post('/v1/api/assistants', assistantData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create assistant.' };
    }
  },

  // Update assistant (super admin only)
  updateAssistant: async (assistantId, assistantData) => {
    try {
      const response = await api.put(`/v1/api/assistants/${assistantId}`, assistantData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update assistant.' };
    }
  },

  // Delete assistant (super admin only) - soft delete
  deleteAssistant: async (assistantId) => {
    try {
      const response = await api.delete(`/v1/api/assistants/${assistantId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete assistant.' };
    }
  },

  // Get assistant statistics
  getAssistantStats: async () => {
    try {
      const response = await api.get('/v1/api/assistants/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assistant statistics.' };
    }
  },

  // Get assistant members
  getAssistantMembers: async (assistantId) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}/members`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assistant members.' };
    }
  },

  // Local storage helpers for selected assistant
  setSelectedAssistant: (assistant) => {
    sessionStorage.setItem('selectedAssistant', JSON.stringify(assistant));
  },

  getSelectedAssistant: () => {
    const assistant = sessionStorage.getItem('selectedAssistant');
    return assistant ? JSON.parse(assistant) : null;
  },

  clearSelectedAssistant: () => {
    sessionStorage.removeItem('selectedAssistant');
  }
};

export const usersService = {
  // Get all users (super admin only)
  getAllUsers: async () => {
    try {
      const response = await api.get('/v1/api/users');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users.' };
    }
  },

  // Invite user to assistant
  inviteUser: async (email, role, assistantId) => {
    try {
      const response = await api.post('/v1/api/invite', { 
        email, 
        role, 
        assistant_id: assistantId 
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to send invitation.' };
    }
  }
};

export const promptService = {
  // Create or update prompt (automatically creates new version)
  createOrUpdatePrompt: async (assistantId, content) => {
    try {
      const response = await api.post(`/v1/api/assistants/${assistantId}/prompt`, { content });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to save prompt.' };
    }
  },

  // Get current active prompt
  getCurrentPrompt: async (assistantId) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}/prompt`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch prompt.' };
    }
  },

  // Get all prompt versions
  getPromptVersions: async (assistantId) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}/prompt/versions`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch prompt versions.' };
    }
  },

  // Get specific version
  getVersionByNumber: async (assistantId, versionNumber) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}/prompt/versions/${versionNumber}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch version.' };
    }
  },

  // Set version as active
  setActiveVersion: async (assistantId, versionNumber) => {
    try {
      const response = await api.put(`/v1/api/assistants/${assistantId}/prompt/versions/${versionNumber}/activate`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to activate version.' };
    }
  }
};

export const tokenService = {
  // Create new API token
  createToken: async (name) => {
    try {
      const response = await api.post('/v1/api/tokens', { name });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create token.' };
    }
  },
  
  // List all API tokens
  listTokens: async () => {
    try {
      const response = await api.get('/v1/api/tokens');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch tokens.' };
    }
  },
  
  // Delete API token
  deleteToken: async (tokenId) => {
    try {
      const response = await api.delete(`/v1/api/tokens/${tokenId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete token.' };
    }
  }
};

export const phoneProviderService = {
  // List phone providers (admin only)
  listPhoneProviders: async () => {
    try {
      const response = await api.get('/v1/api/phone-providers');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch phone providers.' };
    }
  }
};

export const callService = {
  // Initiate dialout (admin only)
  initiateDialout: async (dialoutRequest) => {
    try {
      const response = await api.post('/v1/api/dialout', dialoutRequest);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to initiate dialout.' };
    }
  }
};

export const analysisPromptService = {
  // Create or update analysis prompt for an assistant
  createOrUpdateAnalysisPrompt: async (assistantId, content) => {
    try {
      const response = await api.put(`/v1/api/assistants/${assistantId}/analysis-prompt`, { content });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to save analysis prompt.' };
    }
  },

  // Get analysis prompt for an assistant
  getAnalysisPrompt: async (assistantId) => {
    try {
      const response = await api.get(`/v1/api/assistants/${assistantId}/analysis-prompt`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch analysis prompt.' };
    }
  }
};

export default api;
